// AppRoutes — createBrowserRouter с 16 routes (main + privacy + login + 13 protected + 404).
// Protected routes обёрнуты в <ProtectedRoute><AppShell>...</AppShell></ProtectedRoute>.
// Admin routes (audit) — в <RoleGuard requiredRole="ADMIN">.
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppShell } from '@/layout/AppShell';
import { ProtectedRoute } from './ProtectedRoute';
import { RoleGuard } from './RoleGuard';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/dashboard';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ErrorPage } from '@/pages/ErrorPage';
import { ProfilePage } from '@/pages/profile';
import { TasksListPage, TaskCreatePage, TaskDetailPage } from '@/pages/tasks';
import {
  DealsListPage,
  DealDetailPage,
  DealNewPage,
  ContactsListPage,
  ContactDetailPage,
  ContactNewPage,
  CompaniesListPage,
  CompanyDetailPage,
  CompanyNewPage,
} from '@/pages/crm';
import { CalendarPage, DayEventsPage, EventDetailPage, AttendeesListPage, CalendarNewPage } from '@/pages/calendar';
import { LeadsListPage, LeadDetailPage } from '@/pages/leads';
import { PaymentsListPage } from '@/pages/payments';
// V14 W6: раздел «Счета» (/invoices; импорт через barrel pages/invoices).
import { InvoicesListPage } from '@/pages/invoices';
// V14 W9: страница «Отчёты» (/reports; импорт прямой — баррель pages/reports/index.ts в W10).
import { ReportsPage } from '@/pages/reports/ReportsPage';
import { TeamPage } from '@/pages/admin';
import { LandingPage } from '@/pages/landing';
import { PrivacyPage } from '@/pages/privacy';

/** Корневой router приложения. */
export const router = createBrowserRouter([
  // R68 / V12 U-36-A: публичный лендинг вне ProtectedRoute/AppShell (доступен и после логина).
  { path: '/main', element: <LandingPage /> },
  // R67b / V12 U-36-B: публичная политика конфиденциальности (ссылка из чекбокса 152-ФЗ).
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <ProtectedRoute>
        <AppShell>
          <Outlet />
        </AppShell>
      </ProtectedRoute>
    ),
    children: [
      { path: '/', element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: <DashboardPage /> },
      // R39b / U-10: страница «Мой профиль» (self-service PATCH /api/users/me).
      { path: '/profile', element: <ProfilePage /> },

      // Tasks — sub-task E
      { path: '/tasks', element: <TasksListPage /> },
      { path: '/tasks/new', element: <TaskCreatePage /> },
      { path: '/tasks/:id', element: <TaskDetailPage /> },

      // CRM — sub-task F; /new роуты (R17a, D14) — точные пути матчатся раньше /:id.
      { path: '/crm/deals', element: <DealsListPage /> },
      { path: '/crm/deals/new', element: <DealNewPage /> },
      // errorElement на detail-роутах (R25 / баг D25): ErrorPage вместо голого React crash-экрана («Unexpected Application Error!»).
      { path: '/crm/deals/:id', element: <DealDetailPage />, errorElement: <ErrorPage /> },
      { path: '/crm/contacts', element: <ContactsListPage /> },
      { path: '/crm/contacts/new', element: <ContactNewPage /> },
      { path: '/crm/contacts/:id', element: <ContactDetailPage />, errorElement: <ErrorPage /> },
      { path: '/crm/companies', element: <CompaniesListPage /> },
      { path: '/crm/companies/new', element: <CompanyNewPage /> },
      { path: '/crm/companies/:id', element: <CompanyDetailPage />, errorElement: <ErrorPage /> },

      // Leads — V12 U-34-E: /crm/leads (список), /crm/leads/:id (карточка); создание — диалог в списке.
      { path: '/crm/leads', element: <LeadsListPage /> },
      { path: '/crm/leads/:id', element: <LeadDetailPage />, errorElement: <ErrorPage /> },

      // Payments — V12 U-35-C: раздел «Платежи» (список + загрузка чека).
      { path: '/payments', element: <PaymentsListPage /> },

      // V14 W6: раздел «Счета» (список + создание/выставление; домен invoices §3.8).
      { path: '/invoices', element: <InvoicesListPage /> },

      // V14 W9: раздел «Отчёты» (аналитика + XLSX-выгрузка; скоуп режет сервер).
      { path: '/reports', element: <ReportsPage /> },

      // Calendar — sub-task G
      { path: '/calendar', element: <CalendarPage /> },
      { path: '/calendar/new', element: <CalendarNewPage /> },
      // R32 / U-8: маршрут дня ПЕРЕД /calendar/events/:id — клик по дню ведёт на список
      // событий дня, а не на event-detail (дата ≠ UUID события).
      { path: '/calendar/day/:date', element: <DayEventsPage /> },
      { path: '/calendar/events/:id', element: <EventDetailPage /> },
      { path: '/calendar/events/:id/attendees', element: <AttendeesListPage /> },

      // Admin — placeholder (sub-task H); R40 / U-11: раздел «Команда».
      {
        path: '/admin',
        element: (
          <RoleGuard requiredRole="admin">
            <Outlet />
          </RoleGuard>
        ),
        children: [
          { path: 'audit-events', element: <DashboardPage /> },
          { path: 'team', element: <TeamPage /> },
        ],
      },
    ],
  },
  { path: '/403', element: <ErrorPage /> },
  { path: '*', element: <NotFoundPage /> },
]);
